// Skills B (owner 2026-11-02 'Make them all'): one hook for the view (view.ts) over the four new technique renderers —
// Snare Constellation (snareFx.ts), Wayward Comet (cometFx.ts), Binding Rings (ringsFx.ts), Gravity Grip (gripFx.ts).
// Render-only: reads Sim state and SimEvents, never writes them.
// Hero animation uses EXISTING approved gestures only (owner rule: new poses need a preview + approval first):
//  - Wayward Comet throw, Binding Rings cast and Snare Constellation scatter: the live one-arm thrust clips (bolt_r / bolt_l,
//    the same reuse as Finger Beam) on the authored hero
//  - Snare slam and the Gravity Grip lift + hold: the approved hands-forward key (the Nova Lance release, as Flare Barrage /
//    Rift Mortar reuse it), held toward the spot
//  - the Gravity Grip slam: the approved Pulse Nova squat-and-push
import type * as THREE from 'three/webgpu';
import type { Sim, SimEvent } from '../core/sim';
import type { Fx } from './fx';
import type { GroundGlow } from './groundGlow';
import type { GltfHero } from './heroGltf';
import { ACTIVE_LANCE, LANCE_POSES } from './heroPoses';
import type { Lightning } from './lightning';
import type { HeroRig } from './models';
import { CometFx } from './cometFx';
import { GripFx } from './gripFx';
import { RingsFx } from './ringsFx';
import { SnareFx } from './snareFx';

export class SkillsBFx {
  readonly snare: SnareFx; readonly comet: CometFx; readonly rings: RingsFx; readonly grip: GripFx;
  /** freeze-frame the view should apply (taken each frame) */
  hitstop = 0;
  /** 0..1 hands-forward pose weight and its world yaw (view.ts applies them to the hero) */
  pose = 0; aimYaw = 0;
  /** bumps when a Gravity Grip slam should play the Pulse Nova squat-and-push */
  slamSeq = 0;
  private thrown = 0; private ringsSeq = 0; private snareCasts = 0;

  constructor(scene: THREE.Scene, fx: Fx, bolts: Lightning, glow: GroundGlow, groundAt: (x: number, z: number) => number, burn: (x: number, z: number, r: number) => void) {
    this.snare = new SnareFx(scene, fx, bolts, glow, groundAt, burn);
    this.comet = new CometFx(scene, fx, bolts, glow, groundAt, burn);
    this.rings = new RingsFx(scene, fx, bolts, glow, groundAt);
    this.grip = new GripFx(scene, fx, glow, groundAt, burn);
  }

  event(ev: SimEvent, sim: Sim): void {
    switch (ev.t) {
      case 'snare': case 'snareSlam': case 'snareCollapse': this.snare.event(ev, sim); break;
      case 'comet': case 'cometHit': case 'cometEnd': this.comet.event(ev, sim); break;
      case 'rings': case 'ringBreak': this.rings.event(ev, sim); break;
      case 'grip': case 'gripSlam': this.grip.event(ev, sim); this.hitstop = Math.max(this.hitstop, this.grip.hitstop); this.grip.hitstop = 0; break;
    }
  }

  /** per frame; gltf = the authored hero when it is the drawn model */
  update(sim: Sim, dt: number, cam: THREE.Camera, gltf: GltfHero | null): void {
    this.snare.update(sim, dt, cam); this.comet.update(sim, dt, cam); this.rings.update(sim, dt); this.grip.update(sim, dt, cam);
    // gestures (existing clips only)
    const h = gltf && gltf.ready && gltf.root.visible ? gltf : null;
    if (this.comet.throwSeq !== this.thrown) { this.thrown = this.comet.throwSeq; if (h?.allows('bolt_r')) h.playAction('bolt_r', this.comet.throwYaw); }
    if (this.rings.castSeq !== this.ringsSeq) { this.ringsSeq = this.rings.castSeq; if (h?.allows('bolt_l')) h.playAction('bolt_l', this.rings.castYaw); }
    if (this.snare.casts !== this.snareCasts) { this.snareCasts = this.snare.casts; if (h?.allows('bolt_l')) h.playAction('bolt_l', this.snare.aimYaw); }
    if (this.grip.slamSeq !== this.slamSeq) this.slamSeq = this.grip.slamSeq;
    const snareW = this.snare.hold > 0 ? 1 : 0;
    this.pose = Math.max(this.grip.pose, this.pose + (snareW - this.pose) * Math.min(1, dt * (snareW ? 18 : 7)));
    this.aimYaw = this.grip.pose > 0.05 ? this.grip.aimYaw : this.snare.aimYaw;
  }

  glowPools(g: GroundGlow, sim: Sim): void { this.snare.glowPools(g, sim); this.comet.glowPools(g, sim); this.rings.glowPools(g, sim); this.grip.glowPools(g, sim); }

  /** Hands-forward pose for the procedural rig: the approved Nova Lance release key, arms only (same reuse as Flare Barrage). */
  poseRig(rig: HeroRig): void {
    const w = this.pose; if (w <= 0.01) return;
    const k = LANCE_POSES[ACTIVE_LANCE].thrust, a = rig.armR.rotation, b = rig.armL.rotation;
    a.set(a.x + (k.armR[0] - a.x) * w, a.y + (k.armR[1] - a.y) * w, a.z + (k.armR[2] - a.z) * w);
    b.set(b.x + (k.armL[0] - b.x) * w, b.y + (k.armL[1] - b.y) * w, b.z + (k.armL[2] - b.z) * w);
    rig.foreR.rotation.x += (k.foreR - rig.foreR.rotation.x) * w; rig.foreL.rotation.x += (k.foreL - rig.foreL.rotation.x) * w;
  }

  clear(): void { this.snare.clear(); this.comet.clear(); this.rings.clear(); this.grip.clear(); this.pose = 0; this.hitstop = 0; }
}
